import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { BACKEND_NODE_URL } from '../config/constants';
import './CompanyPlanCard.css';
import AppIcon from './icons/AppIcon';

const CompanyPlanCard = () => {
  const navigate = useNavigate();
  const { user, isAuthInitialized, reloadAuth } = useAuth();
  const [loading, setLoading] = useState(true);
  const [planData, setPlanData] = useState(null);
  const [creditsData, setCreditsData] = useState(null);
  const [error, setError] = useState('');

  const loadPlanData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      let currentUser = user;

      if (!currentUser?.id && reloadAuth) {
        try {
          await reloadAuth();
          currentUser = user;
        } catch (e) {
          console.warn('[CompanyPlanCard] Falha ao recarregar auth:', e);
        }
      }

      if (!currentUser?.id) {
        setPlanData(null);
        setCreditsData(null);
        setError('Não foi possível identificar o usuário autenticado.');
        return;
      }

      const params = new URLSearchParams({ userId: currentUser.id });
      if (currentUser.company_id) {
        params.append('companyId', currentUser.company_id);
      }

      const response = await fetch(`${BACKEND_NODE_URL}/api/company/plan?${params.toString()}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || 'Falha ao carregar plano.');
      }

      const { subscription, credits } = await response.json();

      if (!subscription) {
        setPlanData(null);
        setCreditsData(null);
        setError('Nenhum plano encontrado para esta empresa.');
        return;
      }

      setPlanData(subscription);
      setCreditsData(credits || null);
    } catch (err) {
      console.error('[CompanyPlanCard] Erro ao carregar plano:', err);
      setPlanData(null);
      setCreditsData(null);
      setError(err?.message || 'Erro ao carregar plano.');
    } finally {
      setLoading(false);
    }
  }, [reloadAuth, user]);

  useEffect(() => {
    if (isAuthInitialized) {
      loadPlanData();
    }
  }, [isAuthInitialized, loadPlanData]);

  if (loading) {
    return (
      <div className="company-plan-card loading">
        <AppIcon name="hourglass_empty" />
      </div>
    );
  }

  if (!planData) {
    return (
      <div className="company-plan-card empty">
        <div className="plan-card-header">
          <div className="plan-card-icon">
            <AppIcon name="workspace_premium" />
          </div>
          <div className="plan-card-info">
            <h4>Plano Atual</h4>
            <h3>Sem plano ativo</h3>
          </div>
        </div>
        <p style={{ color: '#9bb3c7', marginTop: '0.5rem' }}>{error || 'Nenhum plano encontrado para esta empresa.'}</p>
        <button 
          className="plan-card-button"
          onClick={() => navigate('/planos')}
        >
          <AppIcon name="shopping_cart" />
          Ver planos disponíveis
        </button>
      </div>
    );
  }

  const plan = planData.plans;
  const planStatus = planData.status || 'inactive';

  return (
    <div className="company-plan-card">
      <div className="plan-card-header">
        <div className="plan-card-icon">
          <AppIcon name="workspace_premium" />
        </div>
        <div className="plan-card-info">
          <h4>Plano Atual</h4>
          <h3>{plan?.name || 'Plano indefinido'}</h3>
          <span className="plan-status-chip">{planStatus === 'active' ? 'Ativo' : 'Inativo'}</span>
        </div>
      </div>

      <div className="plan-card-details">
        <div className="plan-detail">
          <AppIcon name="account_balance_wallet" />
          <span>{plan.monthly_credits === 999999999 ? 'Ilimitado' : plan.monthly_credits.toLocaleString('pt-BR')} créditos/mês</span>
        </div>
        <div className="plan-detail">
          <AppIcon name="chat" />
          <span>{plan.max_whatsapp_connections === 999999 ? 'Ilimitadas' : plan.max_whatsapp_connections} conexões WhatsApp</span>
        </div>
        <div className="plan-detail">
          <AppIcon name="smart_toy" />
          <span>{plan.max_agents === 999999 ? 'Ilimitados' : plan.max_agents} agentes IA</span>
        </div>
        <div className="plan-detail">
          <AppIcon name="dashboard" />
          <span>{plan.max_projects === 999999 ? 'Ilimitados' : plan.max_projects} projetos</span>
        </div>
        <div className="plan-detail">
          <AppIcon name="event" />
          <span>Válido até {planData.expires_at ? new Date(planData.expires_at).toLocaleDateString('pt-BR') : '—'}</span>
        </div>
      </div>

      {creditsData && (
        <div className="plan-card-details" style={{ marginTop: '0.5rem' }}>
          <div className="plan-detail">
            <AppIcon name="monetization_on" />
            <span>Créditos disponíveis: {creditsData.credits_available?.toLocaleString('pt-BR') ?? 0}</span>
          </div>
          <div className="plan-detail">
            <AppIcon name="history" />
            <span>Período: {creditsData.period_start ? new Date(creditsData.period_start).toLocaleDateString('pt-BR') : '—'} até {creditsData.period_end ? new Date(creditsData.period_end).toLocaleDateString('pt-BR') : '—'}</span>
          </div>
        </div>
      )}

      <button 
        className="plan-card-button"
        onClick={() => navigate('/configuracoes')}
      >
        <AppIcon name="visibility" />
        Ver Detalhes do Plano
      </button>
    </div>
  );
};

export default CompanyPlanCard;
